import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Mail,
} from 'lucide-react';
import {
  demanderReinitialisation,
  reinitialiserMotDePasse,
  verifierJetonReinitialisation,
} from '../services/api';

/**
 * Mot de passe oublié. La page couvre les deux étapes : sans jeton dans l'URL
 * elle demande l'adresse, avec un jeton elle fait choisir le nouveau mot de passe.
 */
export default function MotDePasseOubliePage() {
  const { token } = useParams();

  return token ? <ChoisirNouveauMotDePasse token={token} /> : <DemanderLeLien />;
}

/* ----------------------------- Étape 1 : demande ---------------------------- */

function DemanderLeLien() {
  const [email, setEmail] = useState('');

  const mutation = useMutation({
    mutationFn: () => demanderReinitialisation(email),
  });

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-md mx-auto pb-24 lg:pb-0">
      <div className="text-center space-y-2">
        <KeyRound className="w-10 h-10 text-primary-600 mx-auto" />
        <h1 className="text-2xl lg:text-3xl font-bold">Mot de passe oublié</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Indiquez votre adresse e-mail. Si un compte y est associé, vous recevrez
          un lien pour choisir un nouveau mot de passe.
        </p>
      </div>

      {mutation.isSuccess ? (
        <div className="card bg-green-50 border-green-200 text-green-800 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold">Demande enregistrée</p>
            <p className="mt-1">{mutation.data?.message}</p>
            <p className="mt-2 text-green-700">
              Le lien est valable une heure. Pensez à regarder vos indésirables.
            </p>
          </div>
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <div className="relative">
            <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input
              className="input pl-10"
              type="email"
              placeholder="Votre adresse e-mail"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-600 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {(mutation.error as Error).message}
            </p>
          )}

          <button
            type="submit"
            disabled={mutation.isPending || !email.includes('@')}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Recevoir le lien
          </button>
        </form>
      )}

      <p className="text-center text-sm">
        <Link to="/login" className="text-primary-700 hover:underline">
          Retour à la connexion
        </Link>
      </p>
    </div>
  );
}

/* ---------------- Étape 2 : choix du nouveau mot de passe ---------------- */

function ChoisirNouveauMotDePasse({ token }: { token: string }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [visible, setVisible] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const jetonQuery = useQuery({
    queryKey: ['reinitialisation', token],
    queryFn: () => verifierJetonReinitialisation(token),
    retry: false,
  });

  const longueurMin = jetonQuery.data?.longueurMinMotDePasse ?? 8;

  const mutation = useMutation({
    mutationFn: () => reinitialiserMotDePasse(token, password, confirmation),
    onSuccess: () => {
      setErreur(null);
      // Laisse le temps de lire la confirmation avant la page de connexion.
      setTimeout(() => navigate('/login'), 2500);
    },
    onError: (error: Error) => setErreur(error.message),
  });

  const valider = (): string | null => {
    if (password.length < longueurMin) {
      return `Le mot de passe doit contenir au moins ${longueurMin} caractères.`;
    }
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      return 'Le mot de passe doit contenir au moins une lettre et un chiffre.';
    }
    if (password !== confirmation) {
      return 'Les deux mots de passe ne correspondent pas.';
    }
    return null;
  };

  if (jetonQuery.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (jetonQuery.isError) {
    return (
      <div className="p-6 lg:p-10 max-w-md mx-auto space-y-4 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Lien invalide ou expiré</p>
            <p className="text-sm mt-1">{(jetonQuery.error as Error).message}</p>
          </div>
        </div>
        <Link to="/mot-de-passe-oublie" className="btn-primary w-full inline-flex justify-center">
          Demander un nouveau lien
        </Link>
      </div>
    );
  }

  if (mutation.isSuccess) {
    return (
      <div className="p-6 lg:p-10 max-w-md mx-auto pb-24 lg:pb-0">
        <div className="card bg-green-50 border-green-200 text-green-800 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Mot de passe modifié</p>
            <p className="text-sm mt-1">Redirection vers la page de connexion…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-md mx-auto pb-24 lg:pb-0">
      <div className="text-center space-y-2">
        <KeyRound className="w-10 h-10 text-primary-600 mx-auto" />
        <h1 className="text-2xl lg:text-3xl font-bold">Nouveau mot de passe</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Compte : <span className="font-medium text-gray-700 dark:text-gray-300">{jetonQuery.data?.membre?.email}</span>
        </p>
      </div>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          const probleme = valider();
          if (probleme !== null) {
            setErreur(probleme);
            return;
          }
          setErreur(null);
          mutation.mutate();
        }}
      >
        <div className="relative">
          <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            className="input pl-10 pr-10"
            type={visible ? 'text' : 'password'}
            placeholder="Nouveau mot de passe"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setVisible(!visible)}
            className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
            aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          >
            {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        <div className="relative">
          <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            className="input pl-10"
            type={visible ? 'text' : 'password'}
            placeholder="Confirmez le mot de passe"
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Au moins {longueurMin} caractères, dont une lettre et un chiffre.
        </p>

        {erreur && (
          <p className="text-sm text-red-600 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {erreur}
          </p>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          className="btn-primary w-full inline-flex items-center justify-center gap-2"
        >
          {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Enregistrer mon nouveau mot de passe
        </button>
      </form>
    </div>
  );
}
